import { createContext, useContext, useEffect, useState } from "react";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [isAuthenticated, setIsAuthenticated] = useState(!!token);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // ⏬ Cargar perfil al tener token válido
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!token) {
        setUser(null);
        setAuthLoading(false);
        return;
      }

      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.ok) {
          const data = await res.json();
          setUser(data);
        } else {
          logout(); // 🔒 Token inválido, cerramos sesión automáticamente
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        setUser(null);
      } finally {
        setAuthLoading(false); 
      }
    };

    fetchUserProfile();
  }, [token]);

  // 🔄 Mantener isAuthenticated en sincronía
  useEffect(() => {
    setIsAuthenticated(!!token);
  }, [token]);

  // 🧠 Al montar, sincronizar token con localStorage si cambia
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (storedToken && storedToken !== token) {
      setToken(storedToken);
    }
  }, []);

  // 🌐 Escuchar cambios en otras pestañas
  useEffect(() => {
    const handleStorageChange = () => {
      const storedToken = localStorage.getItem("token");
      setToken(storedToken);
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // ✅ Login: guardar token y perfil
  const login = (newToken) => {
    localStorage.setItem("token", newToken);
    setToken(newToken);
    window.dispatchEvent(new Event("authChanged"));
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    window.dispatchEvent(new CustomEvent("userLoggedOut"));
  };

  // 🔁 Al loguearse, avisar para recargar settings privados
  useEffect(() => {
    if (isAuthenticated) {
      window.dispatchEvent(new CustomEvent("userLoggedIn"));
    }
  }, [isAuthenticated]);

  // 👤 Actualizar perfil del usuario en memoria
  const updateUser = (newUserData) => {
    setUser((prevUser) => ({
      ...prevUser,
      ...newUserData,
    }));
    window.dispatchEvent(new Event("authChanged")); // 🔄 fuerza re-render donde se escuche este evento
  };

  return (
    <AuthContext.Provider value={{ token, isAuthenticated, user, authLoading, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
